import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, ArrowRight, Mail, Download, Play, Pause } from "lucide-react";
import WaveSurfer from "wavesurfer.js";
import { CHAPTERS, CREDITS } from "@/pages/Lyrics";
import { api } from "@/lib/api";

/**
 * `/press` — Metamorph press & radio listening room.
 *
 * Unlisted (no public nav link) — the artist hands the URL to press
 * contacts, radio programmers, and editors. Layout mirrors `/tour`,
 * `/chronology`, and `/lyrics` so the page sits naturally inside the
 * Metamorph family: hero with title overlay → short-form bio →
 * full-album player → individual chapter players with collapsible
 * lyric dropdowns → long-form bio → request-download CTA (mailto to
 * gm@theprocess.studio).
 *
 * Audio files live in `/public/audio/` so they can be replaced by
 * dropping new MP3s into that folder without touching React.
 */

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oivt9a3p__L2A0180.jpg";

// MP3 sources — replace these by dropping files into /public/audio/ OR
// by pasting an external URL (e.g. an `emergentagent` asset link or a
// Dropbox `?raw=1` link). The native HTML5 player reads duration from
// the file itself, so no hardcoded length is needed here.
const FULL_ALBUM = {
  label: "Metamorph — Full Length",
  src: "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/3pq1ss3a_metamorph_-_george_maple.mp3",
};

const TRACKS = [
  { label: "Chapter 1",  title: "Up Here",          src: "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/s19w8lqw_metamorph_-_chapter_1_-_up_here_.mp3" },
  { label: "Chapter 2",  title: "Aerofe",           src: "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/znpj29zb_metamorph_-_chapter_2_-_aerofe_.mp3" },
  { label: "Chapter 3",  title: "A Name",           src: "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/is3s2p4d_metamorph_-_chapter_3_-_a_name_.mp3" },
  { label: "Chapter 4",  title: "Tiny Box",         src: "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/ng8rzdye_metamorph_-_chapter_4_-_tiny_box_.mp3" },
  { label: "Chapter 4a", title: "Gravity",          src: "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/qettj6px_metamorph_-_chapter_4a_-_gravity_..mp3" },
  { label: "Chapter 5",  title: "Leave the Winter", src: "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/93j7dxto_metamorph_-_chapter_5_-_leave_the_winter_.mp3" },
];

// Bios — your words, lightly typo-corrected only. Section auto-hides
// when empty. `\n\n` = paragraph break, `\n` = line break.
const SHORT_BIO = `Metamorph is a 9 minute sonic journey with 5 chapters, cinematic and expressive — an exploration of an inner world and its oscillation between the dark and light, physical and metaphysical. Maple's vocals are the centerpiece, rich, powerful and yet vulnerable, storytelling — embellished with strings, orchestral textures and soundscapes built around her distinct tone.`;

const LONG_BIO = `George Maple is an Australian-born, multidisciplinary artist who moves fluidly across genres and mediums — film, music and live performance. Her work lives somewhere between the subterranean depths of the human inner world and the esoteric, unseen realms of the metaphysical, with threads of fantasy woven through. Her voice captivates, drawing you in with a rare ability to float effortlessly between power and vulnerability.

"This piece of work came together quickly and for whatever reason, it wanted to be 9 minutes in length… it feels almost like an extended interlude to me… or somewhat of a portal between where I've been and I suppose where I'm going."

Recorded at The Church Studios in London with cello and piano performed by Nomzeplume, mixed by Simon Cohen (chapters 1–4) and Tyler Scott (chapter 5), mastered by Dale Becker, with artwork by Luke Dubbelde. Released and distributed independently.

You may know Maple from her own work — 'Talk Talk', later reworked by DJ Snake into 'Talk'. You may also know her prolific catalogue of work for other artists — a true songwriter at heart, she has lent her writing and production to Melanie C, Anna Lunoe, Flume, Hayden James, What So Not, Tkay Maidza, Flight Facilities, Jessica Mauboy and DJ Snake, quietly amassing over 1 billion streams and multiple award nominations along the way.

As a performer, George has taken the stage all over the world — from Carmen in Paris and The Enmore in Sydney to guest appearances at Coachella, Lollapalooza and EDC. She has built private conceptual performances for Cartier and Dom Pérignon, and earned recognition for her filmmaking, including the ACS National Gold Award. In 2020, she screened a self-written and directed 24-minute live-performance film at the Sydney Opera House.

"Musically, I've been exploring different spaces within my voice and with my journey in film, I've been exploring sonic worlds and being less concerned with pop structures and more interested in following the thread wherever it may lead."

After time away from music to focus on filmmaking, George returns to the stage to celebrate the release of Metamorph — performed alongside reworked moments from her discography, in hand-picked venues across the globe.`;

// Per-track commentary — keyed by track `title`. Empty values hide the
// "Show notes" dropdown on that track. Fill in any track to surface it.
// `\n\n` = paragraph break, `\n` = line break (handled in render).
const TRACK_NOTES = {
  "Up Here": `A surreal and all too familiar feeling. As a fluid and very sensitive being, the more I began to deconstruct my world and let go of my armor, the more I would find myself floating in the ether, the spiritual and the esoteric… flying away from the lingering emotions I was not yet ready to feel. I remained suspended, not yet ready to anchor, lost in the magic and wonder of the unseen.`,

  "Aerofe": `A continuation of Up Here, this sense of blissfulness, or glimmers — sometimes of soon to be experienced joy, other times a sense of escapism. The sonic thread of the fae-like vocals remains a thread throughout the 9 minute journey. I have been both guided by the metaphysical and somewhat lured into the comfort of being able to disconnect from physical reality.`,

  "A Name": `We are all a sum of many parts and many shades. I developed defaults when moving through the world as an adult for the first time, a harshness and almost domineering energy… an armor to protect and a mechanism to keep me locked in precision, with no room for error. This aspect both served me and hindered me in the context of life at this time. I spent hundreds of hours working through and deconstructing this part in order to both make sense of it, and alchemising this energy into its exalted expression, strength and the ability and resilience to hold a vast amount of space for big ideas.`,

  "Tiny Box": `Once again, thematically somewhat of an escape from the world. I found myself very introverted and isolated after my departure from the music industry. I was literally building worlds cinematically and I would find myself completely wrapped up and not even speaking to a person for days, maybe a week at a time. A part of this was of course, a part of the process of building new skills — however it was also coming from a wounded place. It was only on reflection I realised my isolation was all the hurt coming to the surface. A time I am so so grateful for as I needed the neutrality to look inwards and also to somewhat pause and make everything a bit smaller in order to really feel.`,

  "Gravity": `This song to me felt like the spiral down to earth. I don't know when I decided to somewhat disconnect from the physical realm… but I did… then in a whirlwind of magic only at the beginning of this year I was spun back into the world, meeting people, falling in love with life again.`,

  "Leave the Winter": `After literally years of shadow work, deconstructing conditioning, dismantling my own beliefs and so on… I found myself in a place of joy and wonder, here in London where it all began, making so many new beautiful connections and really truly engaging with the world for the first time in so long. It felt like the finale to this piece wanted to be expansive and more joyful — a reminder of the magic of being able to merge the metaphysical, underworld and physical realms.`,
};

// Downloadable assets surfaced in the "Downloads" section. Items with an
// empty `url` render as a disabled "Coming soon" tile. Drop files into
// /app/frontend/public/press-downloads/ and set the url to the served
// path (e.g. "/press-downloads/george-maple-bio.pdf").
const DOWNLOADS = [
  { key: "bio",         label: "Bio",                url: "/press-downloads/george-maple-bio.pdf",         filename: "george-maple-bio.pdf" },
  { key: "tour-bio",    label: "Tour bio",           url: "/press-downloads/george-maple-tour-bio.pdf",    filename: "george-maple-tour-bio.pdf" },
  { key: "track-bio",   label: "Track-by-track",     url: "/press-downloads/metamorph-track-by-track.pdf", filename: "metamorph-track-by-track.pdf" },
  { key: "manifesto",   label: "Manifesto",          url: "/press-downloads/metamorph-manifesto.pdf",      filename: "metamorph-manifesto.pdf" },
  { key: "cover-art",   label: "Cover art",          url: "", filename: "metamorph-cover-art.zip", external: true },
];

// Past press clippings — each entry surfaces as a card linking out to
// the original article. Empty array = section hidden. Fill with:
//   { outlet: "Pitchfork", headline: "…", url: "…", date: "2025" }
const PAST_PRESS = [];

const REQUEST_EMAIL = "gm@theprocess.studio";
const REQUEST_SUBJECT = "Metamorph — download request";
const REQUEST_BODY =
  "Hi GM,\n\nI'd love access to the Metamorph WAV download for:\n\n— Outlet:\n— Use case (review / radio play / interview / other):\n\nThanks,\n";

// Press images shown in the "Press images" grid. Drop new files into
// /app/frontend/public/press-images/ and add an entry here. `label` is
// the small caption underneath; `src` is the served path; `filename` is
// the suggested download filename (browsers honour the `download` attr).
const PRESS_IMAGES = [
  { label: "Press shot 01", src: "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oockwr4l_Screenshot%202026-06-21%20at%2010.28.30%20PM.png", filename: "george-maple-press-shot-01.png" },
  { label: "Press shot 02", src: "/press-images/press-shot-02.jpg", filename: "george-maple-press-shot-02.jpg" },
  { label: "Press shot 03", src: "/press-images/press-shot-03.jpg", filename: "george-maple-press-shot-03.jpg" },
  { label: "Album artwork", src: "/press-images/album-artwork.jpg", filename: "metamorph-artwork.jpg" },
];

const LS_KEY = "metamorph_press_token";

function PressGate({ children }) {
  /**
   * Inline gate for the /press portal. Mirrors `SiteGate` but uses an
   * isolated localStorage key + endpoint pair (`/api/press/access`) so the
   * VIP site password and the press portal password stay independent.
   */
  const [locked, setLocked] = useState(null);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = (() => { try { return localStorage.getItem(LS_KEY); } catch { return null; } })();
    api.get("/press/access").then(async ({ data }) => {
      if (!data?.locked) { setLocked(false); return; }
      if (!stored) { setLocked(true); return; }
      try {
        const { data: v } = await api.post("/press/access/verify", { token: stored });
        if (v?.ok) { setLocked(false); }
        else { try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ } setLocked(true); }
      } catch { setLocked(true); }
    }).catch(() => setLocked(false));
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/press/access", { password: password.trim() });
      if (data?.ok && data.token) {
        try { localStorage.setItem(LS_KEY, data.token); } catch { /* ignore */ }
        setLocked(false);
      } else {
        setError("Wrong password.");
      }
    } catch { setError("Couldn't reach the server."); }
    finally { setSubmitting(false); }
  };

  if (locked === null) return null;
  if (locked === false) return children;

  return (
    <main
      className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white flex flex-col items-center justify-center px-6 py-10"
      data-testid="press-gate"
    >
      <div className="w-full max-w-sm text-center">
        <h1 className="deco-text text-5xl sm:text-6xl uppercase mb-2" style={{ letterSpacing: "0.1em" }}>
          Metamorph
        </h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-1">
          By George Maple
        </p>
        <p
          className="mb-10 text-xl sm:text-2xl"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            color: "#f3e0a6",
            letterSpacing: "0.02em",
          }}
        >
          Sync + Media Portal
        </p>
        <form onSubmit={onSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="line-input text-center"
            autoFocus
            data-testid="press-gate-input"
          />
          <button
            type="submit"
            disabled={submitting || !password.trim()}
            className="btn-line btn-filled w-full justify-center mt-6 disabled:opacity-30"
            data-testid="press-gate-submit"
          >
            {submitting ? "Checking…" : "Enter"}
          </button>
          {error && <p className="text-rose-300/80 text-xs mt-4 font-mono">{error}</p>}
        </form>
      </div>
    </main>
  );
}

function Paragraphs({ text, className = "" }) {
  return text
    .split("\n\n")
    .filter((p) => p.trim())
    .map((p, i) => (
      <p key={i} className={className}>
        {p.split("\n").map((line, j, arr) => (
          <span key={j}>
            {line}
            {j < arr.length - 1 && <br />}
          </span>
        ))}
      </p>
    ));
}

function fmtTime(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

function AudioRow({ label, title, src, accent = false, onEnded, registerPlayer }) {
  /**
   * SoundCloud-style waveform player built on wavesurfer.js. The actual
   * wave is computed from the file (peaks decoded once when the audio
   * loads) — no fake bars. Brand-gold progress over a dimmer cream wave.
   */
  const waveRef = useRef(null);
  const wsRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!waveRef.current) return;
    const ws = WaveSurfer.create({
      container: waveRef.current,
      // Dim cream wave for the unplayed portion …
      waveColor: "rgba(231, 227, 216, 0.28)",
      // … bright brand-gold for the played portion (SoundCloud-style).
      progressColor: "#f3e0a6",
      cursorColor: "rgba(243, 224, 166, 0.55)",
      cursorWidth: 1,
      barWidth: 2,
      barGap: 2,
      barRadius: 1,
      height: accent ? 72 : 48,
      url: src,
      // `metadata`-equivalent — defers the actual audio download until
      // play is hit, but the peaks are still calculated for the wave.
      backend: "MediaElement",
      mediaControls: false,
    });
    wsRef.current = ws;

    ws.on("ready", () => { setReady(true); setDuration(ws.getDuration()); });
    ws.on("audioprocess", (t) => setCurrent(t));
    ws.on("seeking", (t) => setCurrent(t));
    ws.on("play", () => setPlaying(true));
    ws.on("pause", () => setPlaying(false));
    ws.on("finish", () => {
      setPlaying(false);
      if (onEnded) onEnded();
    });
    if (registerPlayer) registerPlayer({
      play: () => ws.play().catch(() => { /* autoplay blocked, ignored */ }),
      pause: () => ws.pause(),
      seekTo: (t) => ws.setTime(t),
    });
    return () => { try { ws.destroy(); } catch { /* ignore */ } };
  }, [src, accent, onEnded, registerPlayer]);

  const toggle = () => {
    const ws = wsRef.current;
    if (!ws) return;
    if (playing) ws.pause();
    else ws.play().catch(() => { /* autoplay blocked, ignored */ });
  };

  return (
    <div
      className={`py-7 ${accent ? "" : "border-t border-white/[0.08]"}`}
      data-testid={`press-track-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-2">
        {label}
      </p>
      {title && (
        <h3
          className={`deco-text uppercase mb-5 leading-[0.95] ${
            accent ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl"
          }`}
          style={{
            color: "#f3e0a6",
            letterSpacing: "0.08em",
          }}
        >
          {title}
        </h3>
      )}
      <div className="flex items-center gap-4 sm:gap-5">
        <button
          type="button"
          onClick={toggle}
          disabled={!ready}
          aria-label={playing ? "Pause" : "Play"}
          className={`flex-shrink-0 ${accent ? "w-14 h-14" : "w-11 h-11"} rounded-full border border-[#f3e0a6]/55 hover:border-[#f3e0a6] hover:bg-[#f3e0a6]/8 text-[#f3e0a6] flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
          data-testid={`press-audio-toggle-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {playing ? (
            <Pause className={accent ? "w-5 h-5" : "w-4 h-4"} strokeWidth={1.5} fill="#f3e0a6" />
          ) : (
            <Play className={`${accent ? "w-5 h-5" : "w-4 h-4"} ml-[2px]`} strokeWidth={1.5} fill="#f3e0a6" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div ref={waveRef} className="w-full" data-testid={`press-audio-${label.toLowerCase().replace(/\s+/g, "-")}`} />
        </div>
        <div className="flex-shrink-0 font-mono text-[10px] tracking-[0.22em] text-white/50 tabular-nums whitespace-nowrap">
          {fmtTime(current)} <span className="text-white/25">/ {fmtTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}

function NotesDropdown({ notes, idx }) {
  /**
   * Per-track artist commentary dropdown. Renders below the player and
   * sits alongside the lyrics dropdown. Hidden entirely when `notes` is
   * empty so unfinished tracks don't show a dead button.
   */
  const [open, setOpen] = useState(false);
  if (!notes || !notes.trim()) return null;
  return (
    <div className="mt-3" data-testid={`press-notes-dropdown-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 hover:text-[#f3e0a6] transition-colors"
        data-testid={`press-notes-toggle-${idx}`}
        aria-expanded={open}
      >
        {open ? "Hide notes" : "Show notes"}
        {open ? (
          <ChevronUp className="w-3 h-3" strokeWidth={1.5} />
        ) : (
          <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
        )}
      </button>
      {open && (
        <div
          className="mt-5 text-[15px] leading-[1.85] text-white/85 space-y-5"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
        >
          <Paragraphs text={notes} className="" />
        </div>
      )}
    </div>
  );
}

function LyricsDropdown({ chapter, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3" data-testid={`press-lyrics-dropdown-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 hover:text-[#f3e0a6] transition-colors"
        data-testid={`press-lyrics-toggle-${idx}`}
        aria-expanded={open}
      >
        {open ? "Hide lyrics" : "Show lyrics"}
        {open ? (
          <ChevronUp className="w-3 h-3" strokeWidth={1.5} />
        ) : (
          <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
        )}
      </button>
      {open && (
        <div className="mt-5 space-y-5">
          {chapter.verses.map((verse, vi) => (
            <div key={vi}>
              {verse.map((line, li) => (
                <p
                  key={li}
                  className="text-[15px] sm:text-[16px] text-white/85 leading-[1.55]"
                  style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                  }}
                >
                  {line}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Press() {
  return (
    <PressGate>
      <PressContent />
    </PressGate>
  );
}

function PressContent() {
  const mailtoHref = `mailto:${REQUEST_EMAIL}?subject=${encodeURIComponent(
    REQUEST_SUBJECT,
  )}&body=${encodeURIComponent(REQUEST_BODY)}`;

  // Refs into every chapter `<audio>` so we can chain playback —
  // when one ends, the next one starts. Indexed to match TRACKS[].
  const chapterRefs = useRef([]);

  return (
    <main
      className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white"
      data-testid="press-page"
    >
      {/* Hero — same treatment as /tour, /chronology, /lyrics */}
      <div className="relative w-full aspect-[3/2] sm:aspect-[16/9] max-h-[70vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          data-testid="press-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--mm-bg,#050a1a)] via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
          <h1
            className="deco-text text-5xl sm:text-7xl uppercase leading-[0.95] mb-1"
            style={{ letterSpacing: "0.1em" }}
            data-testid="press-title"
          >
            Metamorph
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/55 mb-3">
            By George Maple
          </p>
          <h2
            className="text-2xl sm:text-3xl mb-4"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              color: "#f3e0a6",
              letterSpacing: "0.02em",
              fontWeight: 500,
            }}
          >
            Sync + Media Portal
          </h2>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {/* Short bio — auto-hidden when SHORT_BIO is empty */}
        {SHORT_BIO && SHORT_BIO.trim() && (
          <section data-testid="press-short-bio" className="mb-14 sm:mb-16">
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-4">
              About
            </p>
            <div
              className="text-[15px] leading-[1.85] text-white/85 space-y-5"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
            >
              <Paragraphs text={SHORT_BIO} className="" />
            </div>
          </section>
        )}

        {/* Full-album player */}
        <section data-testid="press-full-album" className="mb-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-5">
            Listen
          </p>
          <AudioRow
            label={FULL_ALBUM.label}
            title="Full length"
            src={FULL_ALBUM.src}
            accent
          />
        </section>

        {/* Individual chapter players with lyric dropdowns + sequential
            auto-play — when a chapter ends, the next one fades in.
            The full-saga player at the top is intentionally NOT chained
            to the chapter sequence (it's the same recording, end-to-end). */}
        <section className="mb-14 sm:mb-16" data-testid="press-chapters">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mt-8 mb-2">
            Chapters
          </p>
          {TRACKS.map((t, i) => {
            // Find matching chapter for lyrics (by title — robust to label
            // variants like "Chapter 4a"). Falls back to undefined which
            // hides the dropdown for instrumental interludes.
            const chapter = CHAPTERS.find((c) => c.title === t.title);
            const handleEnded = () => {
              const nextPlayer = chapterRefs.current[i + 1];
              if (nextPlayer && nextPlayer.play) {
                nextPlayer.seekTo(0);
                nextPlayer.play();
                // Scroll the next player into view so the listener follows.
                const nextEl = document.querySelector(
                  `[data-testid="press-track-${TRACKS[i + 1].label.toLowerCase().replace(/\s+/g, "-")}"]`,
                );
                if (nextEl) nextEl.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            };
            return (
              <div key={t.title}>
                <AudioRow
                  label={t.label}
                  title={t.title}
                  src={t.src}
                  registerPlayer={(player) => { chapterRefs.current[i] = player; }}
                  onEnded={handleEnded}
                />
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                  {chapter && chapter.verses?.length > 0 && (
                    <LyricsDropdown chapter={chapter} idx={i} />
                  )}
                  <NotesDropdown notes={TRACK_NOTES[t.title]} idx={i} />
                </div>
              </div>
            );
          })}
        </section>

        {/* Long-form bio — auto-hidden when LONG_BIO is empty */}
        {LONG_BIO && LONG_BIO.trim() && (
          <section data-testid="press-long-bio" className="mb-14 sm:mb-16">
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-4">
              Bio
            </p>
            <div
              className="text-[15px] leading-[1.85] text-white/85 space-y-5"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
            >
              <Paragraphs text={LONG_BIO} className="" />
            </div>
          </section>
        )}

        {/* Credits */}
        <section data-testid="press-credits" className="mb-14 sm:mb-16">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-4">
            Credits
          </p>
          <dl>
            {CREDITS.map(([label, value]) => (
              <div
                key={label}
                className="grid grid-cols-[200px_1fr] gap-3 py-2 border-b border-white/[0.06] last:border-b-0"
              >
                <dt className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50 pt-1">
                  {label}
                </dt>
                <dd
                  className="text-[15px] text-white/85"
                  style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                  }}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Downloads — bio PDFs, track-by-track, press images, etc.
            Items with empty url render disabled as "Coming soon". Drop
            files into /app/frontend/public/press-downloads/ and update
            the url in the DOWNLOADS array at the top of this file. */}
        <section data-testid="press-downloads" className="mb-14 sm:mb-16">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-5">
            Downloads
          </p>
          <div className="space-y-px">
            {DOWNLOADS.map((d) => {
              const ready = !!(d.url && d.url.trim());
              const isExternal = !!d.external;
              return (
                <a
                  key={d.key}
                  href={ready ? d.url : undefined}
                  download={ready && !isExternal ? d.filename : undefined}
                  target={ready && isExternal ? "_blank" : undefined}
                  rel={ready && isExternal ? "noopener noreferrer" : undefined}
                  aria-disabled={!ready}
                  onClick={(e) => !ready && e.preventDefault()}
                  className={`flex items-center justify-between gap-4 px-5 py-4 bg-white/[0.03] border border-white/10 hover:border-[#f3e0a6]/40 hover:bg-white/[0.06] transition-colors ${
                    ready ? "" : "opacity-50 cursor-not-allowed"
                  }`}
                  data-testid={`press-download-${d.key}`}
                >
                  <span
                    className="text-[16px]"
                    style={{
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      color: "#f3e0a6",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {d.label}
                  </span>
                  <span className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.32em] text-white/60">
                    {ready ? "Download" : "Coming soon"}
                    {ready && <Download className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  </span>
                </a>
              );
            })}
          </div>

          {/* Press images grid — sits inside the Downloads block so all
              press-grabbable assets are in one mental place. */}
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mt-10 mb-5">
            Press images
          </p>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {PRESS_IMAGES.map((img) => (
              <a
                key={img.src}
                href={img.src}
                download={img.filename}
                className="group block"
                data-testid={`press-image-${img.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="aspect-[4/5] bg-white/[0.04] border border-white/[0.06] overflow-hidden relative">
                  <img
                    src={img.src}
                    alt={img.label}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/80">
                      {img.label}
                    </span>
                    <Download className="w-3.5 h-3.5 text-[#f3e0a6]" strokeWidth={1.5} />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Past press — auto-hidden when PAST_PRESS array is empty.
            Each entry: { outlet, headline, url, date, thumbnail? }. */}
        {PAST_PRESS.length > 0 && (
          <section data-testid="press-past" className="mb-14 sm:mb-16">
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-5">
              Past press
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {PAST_PRESS.map((p, i) => (
                <a
                  key={i}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block border border-white/10 hover:border-[#f3e0a6]/40 bg-white/[0.02] hover:bg-white/[0.04] transition-all p-5"
                  data-testid={`press-past-${i}`}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-2">
                    {p.outlet}{p.date ? ` · ${p.date}` : ""}
                  </p>
                  <p
                    className="text-[17px] leading-[1.35] group-hover:text-[#f3e0a6] transition-colors"
                    style={{
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      color: "#e3dfd1",
                    }}
                  >
                    {p.headline}
                  </p>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Instagram feed — placeholder until the Instagram Graph API +
            Facebook Page connection is set up. Wiring this requires an
            access token from the artist's IG Business / Creator account
            (set on the backend so it doesn't leak client-side). */}
        <section data-testid="press-instagram" className="mb-14 sm:mb-16">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-5">
            Instagram
          </p>
          <div className="border border-white/10 bg-white/[0.03] py-14 px-6 text-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
              Feed coming soon
            </p>
          </div>
        </section>

        {/* Request Download CTA */}
        <section data-testid="press-request-download" className="mb-10">
          <a
            href={mailtoHref}
            className="btn-line w-full justify-center text-sm py-4"
            data-testid="press-request-download-button"
          >
            <Mail className="w-4 h-4" strokeWidth={1.4} />
            Request download
            <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
          </a>
          <p className="text-center mt-3 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
            Press &amp; radio enquiries — {REQUEST_EMAIL}
          </p>
        </section>

        <p className="text-center mt-10 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
          <Link to="/" className="hover:text-white/85">
            &larr; Back to Metamorph
          </Link>
        </p>
      </div>

      {/* Wavesurfer container styling — let the canvas breathe with a
          subtle inner glow so the wave doesn't look flat. */}
      <style>{`
        [data-testid^="press-audio-"] canvas {
          opacity: 0.95;
        }
      `}</style>
    </main>
  );
}
